import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE } from "@/lib/site";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Approach", href: "#approach" },
  { label: "Proof", href: "#proof" },
  { label: "Contact", href: "#quote" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-ink-900/85 backdrop-blur-xl border-line" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="flex h-16 md:h-20 items-center justify-between">
          <button
            data-testid="brand-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-baseline gap-2 group"
          >
            <span className="font-display font-black text-xl md:text-2xl tracking-tighter">
              {SITE.brand}
            </span>
            <span className="h-2 w-2 bg-acid transition-transform duration-300 group-hover:scale-150" />
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-get-quote"
              onClick={() => go("#quote")}
              className="font-mono text-[11px] uppercase tracking-[0.2em] border border-acid text-acid px-5 py-2.5 hover:bg-acid hover:text-ink-900 transition-colors"
            >
              Get a quote
            </button>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-ink-900 border-b border-line"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-2xl tracking-tight"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
